import { useEffect, useMemo, useState, type ChangeEvent, type ReactElement } from 'react';
import type { AirportView, OriginDemandAnswer, OriginDemandRowView } from '../../../domain/models/contracts';
import { formatInt } from '../../format';
import './DemandMatrixPanel.css';

export interface DemandMatrixPanelProps {
  /** Airports offered as suggestions. The worker accepts any code in its full database. */
  readonly airports: readonly AirportView[];
  /** Latest worker answer; the panel only uses it when it matches the selected origin. */
  readonly answer: OriginDemandAnswer | null;
  /** Completed sim day. A new day asks again, because demand is recomputed between days. */
  readonly day: number;
  readonly onRequestRow: (originId: string) => void;
}

const IATA_PATTERN = /^[A-Z]{3}$/;
/** Rows shown before the list is cut off; the filter and "show all" reach the rest. */
const ROW_LIMIT = 100;

function formatPax(value: number): string {
  return value >= 10 ? formatInt(Math.round(value)) : value.toFixed(1);
}

function formatShare(pct: number): string {
  return `${pct.toFixed(1)}%`;
}

function formatFactor(value: number): string {
  return value.toFixed(3);
}

interface RankedRow {
  readonly rank: number;
  readonly row: OriginDemandRowView;
}

export function DemandMatrixPanel({ airports, answer, day, onRequestRow }: DemandMatrixPanelProps): ReactElement {
  const [originInput, setOriginInput] = useState<string>('');
  const [filterInput, setFilterInput] = useState<string>('');
  const [showAll, setShowAll] = useState<boolean>(false);

  const originCode = originInput.trim().toUpperCase();
  const originReady = IATA_PATTERN.test(originCode);

  useEffect(() => {
    if (originReady) onRequestRow(originCode);
  }, [originReady, originCode, day, onRequestRow]);

  const airportsById = useMemo(() => new Map(airports.map((airport) => [airport.id, airport] as const)), [airports]);
  const originName = airportsById.get(originCode)?.name;

  const current: OriginDemandAnswer | null =
    originReady && answer !== null && answer.originId === originCode ? answer : null;
  const rows = useMemo<readonly OriginDemandRowView[]>(() => current?.rows ?? [], [current]);

  const totals = useMemo(() => {
    let total = 0;
    let business = 0;
    let leisure = 0;
    let substantial = 0;
    for (const row of rows) {
      total += row.totalPassengersPerDay;
      business += row.businessPassengersPerDay;
      leisure += row.leisurePassengersPerDay;
      if (row.totalPassengersPerDay >= 1) substantial += 1;
    }
    return {
      total,
      business,
      leisure,
      businessSharePct: total > 0 ? (business / total) * 100 : 0,
      substantial,
    };
  }, [rows]);

  // Rank is the position in the full row, so it stays put while the filter narrows the list.
  const filtered = useMemo<readonly RankedRow[]>(() => {
    const needle = filterInput.trim().toUpperCase();
    const ranked = rows.map((row, index): RankedRow => ({ rank: index + 1, row }));
    if (needle === '') return ranked;
    return ranked.filter(
      ({ row }) => row.destinationId.includes(needle) || row.destinationName.toUpperCase().includes(needle),
    );
  }, [rows, filterInput]);

  const visible = showAll ? filtered : filtered.slice(0, ROW_LIMIT);

  const onOriginChange = (event: ChangeEvent<HTMLInputElement>): void => setOriginInput(event.target.value);
  const onFilterChange = (event: ChangeEvent<HTMLInputElement>): void => setFilterInput(event.target.value);

  return (
    <div className="demand-matrix">
      <header className="dm-header">
        <div>
          <h2 className="dm-title">Global Demand Matrix</h2>
          <p className="dm-subtitle">
            Daily passenger demand from one origin to every airport in the database, strongest first.
          </p>
        </div>
        <label className="dm-field">
          <span className="dm-field-label">Origin</span>
          <input
            className="dm-input"
            type="text"
            list="dm-airport-codes"
            value={originInput}
            onChange={onOriginChange}
            placeholder="LHR"
            maxLength={3}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <datalist id="dm-airport-codes">
          {airports.map((airport) => (
            <option key={airport.id} value={airport.id} label={`${airport.name} · ${airport.city}, ${airport.country}`} />
          ))}
        </datalist>
      </header>

      {!originReady && (
        <p className="dm-hint">Enter a three-letter airport code to see its demand row.</p>
      )}

      {originReady && current === null && <p className="dm-hint">Computing demand from {originCode}…</p>}

      {current !== null && current.rows.length === 0 && (
        <p className="dm-error">{originCode} is not in the airport database, so it has no demand row.</p>
      )}

      {current !== null && current.rows.length > 0 && (
        <>
          <section className="dm-summary" aria-label="Origin summary">
            <div className="dm-stat">
              <span className="dm-stat-label">Origin</span>
              <span className="dm-stat-value">{originCode}</span>
              <span className="dm-stat-note">{originName ?? 'Name unavailable'}</span>
            </div>
            <div className="dm-stat">
              <span className="dm-stat-label">Passengers a day</span>
              <span className="dm-stat-value dm-accent">{formatInt(Math.round(totals.total))}</span>
              <span className="dm-stat-note">
                Business {formatInt(Math.round(totals.business))} · Leisure {formatInt(Math.round(totals.leisure))}
              </span>
            </div>
            <div className="dm-stat">
              <span className="dm-stat-label">Business share</span>
              <span className="dm-stat-value">{formatShare(totals.businessSharePct)}</span>
              <span className="dm-stat-note">Weighted across all destinations</span>
            </div>
            <div className="dm-stat">
              <span className="dm-stat-label">Destinations ≥ 1 a day</span>
              <span className="dm-stat-value">{formatInt(totals.substantial)}</span>
              <span className="dm-stat-note">of {formatInt(rows.length)} airports in the database</span>
            </div>
          </section>

          <div className="dm-toolbar">
            <label className="dm-field dm-field--grow">
              <span className="dm-field-label">Filter destinations</span>
              <input
                className="dm-input"
                type="search"
                value={filterInput}
                onChange={onFilterChange}
                placeholder="Code or name"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <p className="dm-note">
              Day {formatInt(current.day)} · gravity constant K = {current.gravityConstant.toExponential(2)}
            </p>
          </div>

          <div className="dm-table-wrap">
            <table className="dm-table">
              <thead>
                <tr>
                  <th scope="col" className="dm-num">#</th>
                  <th scope="col">Destination</th>
                  <th scope="col" className="dm-num">Distance km</th>
                  <th scope="col" className="dm-num">Pax / day</th>
                  <th scope="col" className="dm-num">Business</th>
                  <th scope="col" className="dm-num">Leisure</th>
                  <th scope="col" className="dm-num">Business %</th>
                  <th scope="col" className="dm-num">Gravity term</th>
                  <th scope="col" className="dm-num">Distance factor</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(({ rank, row }) => (
                  <tr key={row.destinationId}>
                    <td className="dm-num dm-muted">{rank}</td>
                    <th scope="row">
                      <span className="dm-code">{row.destinationId}</span> {row.destinationName}
                    </th>
                    <td className="dm-num">{formatInt(Math.round(row.distanceKm))}</td>
                    <td className="dm-num dm-accent">{formatPax(row.totalPassengersPerDay)}</td>
                    <td className="dm-num">{formatPax(row.businessPassengersPerDay)}</td>
                    <td className="dm-num">{formatPax(row.leisurePassengersPerDay)}</td>
                    <td className="dm-num">{formatShare(row.businessSharePct)}</td>
                    <td className="dm-num">{formatPax(row.rawGravityPassengersPerDay)}</td>
                    <td className="dm-num">{formatFactor(row.distanceAttenuation)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && <p className="dm-note dm-empty">No destination matches “{filterInput.trim()}”.</p>}
          </div>

          <div className="dm-footer">
            <p className="dm-note">
              Pax / day = gravity term × distance factor, capped by the saturation ceiling. The business and leisure
              columns split that total by business share.
            </p>
            {!showAll && filtered.length > ROW_LIMIT && (
              <button type="button" className="btn btn-small" onClick={() => setShowAll(true)}>
                Show all {formatInt(filtered.length)}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
